// @ts-nocheck
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    getOrderDetails,
    putApprovalStatus,
    putServiceFailure,
} from "../../../api/service-failures.api.ts";
import { useRoles } from "../../../context/roles-context.tsx";
import useAccountEnhanced from "../../../hooks/use-account-enhanced.ts";
import ConfirmationDialog from "../components/ConfirmationDialog.tsx";
import SFAtFault from "../components/SFAtFault.tsx";
import SFAttachments from "../components/SFAttachments.tsx";

interface AtFault {
    EmployeeCode: string;
    Role: string;
    ReasonCategory: string;
}

function ServiceFailure() {
    const { orderNumber } = useParams<{ orderNumber: string }>();

    const [atFaults, setAtFaults] = useState<AtFault[]>();

    const [orderDetails, setOrderDetails] = useState(null);
    const [serviceFailureStatus, setServiceFailureStatus] = useState("");
    const [isRejectOpen, setRejectOpen] = useState(false);
    const [isApproveOpen, setApproveOpen] = useState(false);
    const roles = useRoles();
    const accessCSM = roles.checkHasRole("ServiceFailure.CSM");
    const accessOpsMgmt = roles.checkHasRole("ServiceFailure.OpsMgmt");
    const accountEnhanced = useAccountEnhanced();
    const navigate = useNavigate();

    useEffect(() => {
        handleGetOrderDetails();
    }, [orderNumber]);

    const handleAtFaultsChange = (newAtFaults: AtFault[]) => {
        setAtFaults(newAtFaults);
    };

    // order details
    const handleGetOrderDetails = async () => {
        try {
            const orderDetailsResult = await getOrderDetails({ orderNumber });
            setOrderDetails(orderDetailsResult);
            setServiceFailureStatus(
                orderDetailsResult.ServiceFailure.Status +
                    (orderDetailsResult.ServiceFailure.CSM_Approval
                        ?.ApprovalStatus === "Approved"
                        ? ", Approved by CSM"
                        : ""),
            );
        } catch (error) {
            toast.error(
                error instanceof Error ? error.message : "An error occurred",
            );
        }
    };

    const handlePutApprovalStatus = async (
        approvalState: string,
        approvalNotes: string,
    ) => {
        try {
            await putApprovalStatus(
                { orderNumber },
                {
                    approvalState,
                    approvalNotes,
                    submittedBy: accountEnhanced.me.employeeCode.toUpperCase(),
                },
            );
        } catch (error) {
            console.error("Error fetching data: ", error);
        }
    };

    const handlePutServiceFailure = async (draftComplete: boolean) => {
        const sfType: string =
            orderDetails.ServiceFailure.ServiceFailureType || "Manual";
        const payload = {
            ServiceFailureType: sfType,
            Explanation: "",
            EmployeeSubmitted: accountEnhanced.me.employeeCode.toUpperCase(),
            DraftComplete: draftComplete,
            AtFault: atFaults,
        };

        try {
            const putServiceFailureResponse = await putServiceFailure(
                { orderNumber },
                {
                    serviceFailureType: sfType,
                    employeeSubmitted:
                        accountEnhanced.me.employeeCode.toUpperCase(),
                    draftComplete,
                    atFaults,
                },
            );

            if (putServiceFailureResponse.Status === "error") {
                toast.error(`ERROR: ${putServiceFailureResponse.Message}`);
            } else if (putServiceFailureResponse.Status === "OK") {
                const successMessage = draftComplete
                    ? "Corrective Action Submitted"
                    : "Draft Saved";
                toast.success(`SUCCESS: ${successMessage}`);
            }
        } catch (error) {
            console.error("Error fetching data: ", error);
        }
    };

    const handleHome = () => {
        navigate(`/serviceFailures`);
    };

    const handleApproveConfirm = () => {
        setApproveOpen(false);
        var approvalState = accessCSM
            ? "approved_csm"
            : accessOpsMgmt
            ? "approved_ops"
            : "";
        handlePutApprovalStatus(approvalState, "");
    };

    const handleApproveCancel = () => {
        setApproveOpen(false);
    };

    const handleRejectConfirm = (inputValue?: string) => {
        setRejectOpen(false);
        var approvalState = accessCSM
            ? "rejected_csm"
            : accessOpsMgmt
            ? "rejected_ops"
            : "";
        handlePutApprovalStatus(approvalState, inputValue || "");
    };

    const handleRejectCancel = () => {
        setRejectOpen(false);
    };

    return (
        <div className="p-8 h-full bg-gray-800 relative">
            <div className="text-gray-50 text-2xl font-semibold text-center mb-4">
                Corrective Action Form
            </div>

            {/* Back */}
            <Link className="btn btn-primary absolute left-8 top-4" to="..">
                Back
            </Link>

            {/* Manager Aproval/Reject */}
            {(accessCSM || accessOpsMgmt) && (
                <div className="absolute top-4 right-8">
                    <div className="space-x-4">
                        <button
                            className="btn btn-success"
                            onClick={() => setApproveOpen(true)}
                        >
                            Approve
                        </button>
                        <button
                            className="btn btn-error"
                            onClick={() => setRejectOpen(true)}
                        >
                            Reject
                        </button>
                    </div>
                    <ConfirmationDialog
                        isOpen={isApproveOpen}
                        message="You are about to approve the following corrective action. Are you sure?"
                        showInput={false}
                        onConfirm={handleApproveConfirm}
                        onCancel={handleApproveCancel}
                    />
                    <ConfirmationDialog
                        isOpen={isRejectOpen}
                        message="You are about to reject the following corrective action. Are you sure?"
                        showInput={true}
                        onConfirm={handleRejectConfirm}
                        onCancel={handleRejectCancel}
                    />
                </div>
            )}

            <div>
                {orderDetails && (
                    <div className="text-white">
                        <div className="flex space-x-4">
                            <div className="card bg-gray-700">
                                <div className="card-body">
                                    <div className="card-title">
                                        Order Information
                                    </div>
                                    <table className="table mb-4">
                                        <thead className="text-gray-50 bg-gray-600">
                                            <tr>
                                                <th>Order Status</th>
                                                <th>Order Datetime</th>
                                                <th>Order Division</th>
                                                <th>Order Zone</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {orderDetails.Order.Status}
                                                </td>
                                                <td>
                                                    {dayjs(
                                                        orderDetails.Order
                                                            .OrderDatetime,
                                                    ).format(
                                                        "MM/DD/YYYY h:mmA",
                                                    )}
                                                </td>
                                                <td>
                                                    {
                                                        orderDetails.Order
                                                            .OrderDivision
                                                    }
                                                </td>
                                                <td>
                                                    {orderDetails.Order.Zone}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <table className="table">
                                        <thead className="text-gray-50 bg-gray-600">
                                            <tr>
                                                <th>Customer</th>
                                                <th>CSR</th>
                                                <th>Order</th>
                                                <th>Origin</th>
                                                <th>Destination</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {orderDetails.Order
                                                        .BillToCustomerCode +
                                                        " - " +
                                                        orderDetails.Order
                                                            .BillToCustomerName}
                                                </td>
                                                <td>
                                                    {orderDetails.Order
                                                        .CSREmployeeCode +
                                                        " - " +
                                                        orderDetails.Order
                                                            .CSREmployeeName}
                                                </td>
                                                <td>
                                                    {
                                                        orderDetails.Order
                                                            .OrderNumber
                                                    }
                                                </td>
                                                <td>
                                                    {orderDetails.Order
                                                        .LoadAt_CustomerCode +
                                                        " - " +
                                                        orderDetails.Order
                                                            .LoadAt_CustomerName +
                                                        " - " +
                                                        orderDetails.Order
                                                            .OriginCity +
                                                        ", " +
                                                        orderDetails.Order
                                                            .OriginState}
                                                </td>
                                                <td>
                                                    {orderDetails.Order
                                                        .Consignee_CustomerCode +
                                                        " - " +
                                                        orderDetails.Order
                                                            .Consignee_CustomerName +
                                                        " - " +
                                                        orderDetails.Order
                                                            .DestinationCity +
                                                        ", " +
                                                        orderDetails.Order
                                                            .DestinationState}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="flex-1 space-y-4">
                                {/* Service Failure Status */}
                                <div className="card card-compact bg-gray-700">
                                    <div className="card-body">
                                        <div className="card-title">
                                            Service Failure Status
                                        </div>
                                        <div className="badge badge-info">
                                            {serviceFailureStatus}
                                        </div>
                                    </div>
                                </div>
                                {/* Approval Status */}
                                <div className="card card-compact bg-gray-700">
                                    <div className="card-body">
                                        <div className="card-title">
                                            Approval Status
                                        </div>
                                        <div className="flex justify-between">
                                            <span>CSM:</span>
                                            <span>
                                                {orderDetails.ServiceFailure
                                                    .CSM_Approval
                                                    ?.ApprovalStatus +
                                                    " - " +
                                                    orderDetails.ServiceFailure
                                                        .CSM_Approval
                                                        ?.EmployeeCode}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Submitted Datetime:</span>
                                            <span>
                                                {!orderDetails.ServiceFailure.CSM_Approval?.TimeStamp?.trim()
                                                    ? "Pending"
                                                    : orderDetails
                                                          .ServiceFailure
                                                          .CSM_Approval
                                                          .TimeStamp}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Ops Mgmt:</span>
                                            <span>
                                                {orderDetails.ServiceFailure
                                                    .OpsManager_Approval
                                                    ?.ApprovalStatus +
                                                    " - " +
                                                    orderDetails.ServiceFailure
                                                        .OpsManager_Approval
                                                        ?.EmployeeCode}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Submitted Datetime:</span>
                                            <span>
                                                {!orderDetails.ServiceFailure.OpsManager_Approval?.TimeStamp?.trim()
                                                    ? "Pending"
                                                    : orderDetails
                                                          .ServiceFailure
                                                          .OpsManager_Approval
                                                          ?.TimeStamp}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Remarks:</span>{" "}
                                            <span>
                                                {
                                                    orderDetails.ServiceFailure
                                                        .ApprovalNotes
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex mt-4">
                            <div className="card card-compact bg-gray-700">
                                <div className="card-body">
                                    <div className="card-title">
                                        Stop Details
                                    </div>
                                    <div className="flex">
                                        <div className="mr-3 space-y-2">
                                            <div className="font-semibold">
                                                Stop Type:
                                            </div>
                                            <div className="font-semibold">
                                                Stop Seq:
                                            </div>
                                            <div className="font-semibold">
                                                Failure ID:
                                            </div>
                                            <div className="font-semibold">
                                                Stop Appt:
                                            </div>
                                            <div className="font-semibold">
                                                Stop Arrival:
                                            </div>
                                            <div className="font-semibold">
                                                Unit:
                                            </div>
                                            <div className="font-semibold">
                                                Div/Fleet:
                                            </div>
                                            <div className="font-semibold">
                                                Fleet Manager:
                                            </div>
                                        </div>
                                        {orderDetails.Order.Stops.map(
                                            (detail, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        marginRight: "10px",
                                                    }}
                                                    className="space-y-2"
                                                >
                                                    <div>{detail.StopType}</div>
                                                    <div>
                                                        {detail.StopSequence}
                                                    </div>
                                                    <div
                                                        className={clsx(
                                                            detail.StopFailure?.trim()
                                                                ? "badge badge-error"
                                                                : "badge badge-ghost",
                                                        )}
                                                    >
                                                        {!detail.StopFailure?.trim()
                                                            ? "None"
                                                            : detail.StopFailure}
                                                    </div>
                                                    <div>
                                                        {dayjs(
                                                            detail.LateApptDatetimeStopff,
                                                        ).format(
                                                            "MM/DD/YYYY h:mmA",
                                                        )}
                                                    </div>
                                                    <div>
                                                        {dayjs(
                                                            detail.StopArrivalTimestamp,
                                                        ).format(
                                                            "MM/DD/YYYY h:mmA",
                                                        )}
                                                    </div>
                                                    <div>
                                                        {detail.UnitNumber}
                                                    </div>
                                                    <div>
                                                        {detail.UnitDivision +
                                                            " - " +
                                                            detail.UnitFleet}
                                                    </div>
                                                    <div>
                                                        {
                                                            detail.UnitFleetManager
                                                        }
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="card card-compact bg-gray-700 ml-auto mr-4">
                                <div className="card-body">
                                    <SFAtFault
                                        initialAtFaults={
                                            orderDetails.ServiceFailure.AtFault
                                        }
                                        onAtFaultsChange={handleAtFaultsChange}
                                    />
                                </div>
                            </div>
                            <div className="card card-compact bg-gray-700">
                                <div className="card-body">
                                    <SFAttachments orderNumber={orderNumber} />
                                    <div className="space-x-2 mt-auto">
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() =>
                                                handlePutServiceFailure(false)
                                            }
                                        >
                                            Save Draft
                                        </button>
                                        <button
                                            className="btn btn-sm btn-success"
                                            onClick={() =>
                                                handlePutServiceFailure(true)
                                            }
                                            disabled={atFaults?.length === 0}
                                            title={
                                                atFaults?.length === 0
                                                    ? "Cannot submit, missing At Fault Fields"
                                                    : ""
                                            }
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ServiceFailure;
